import { useState } from "react";
import {
    Video,
    DollarSign,
    Users,
    BarChart3,
    Shield,
    Headphones,
    Check,
    ChevronRight,
    Sparkles
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const FeaturesShowcaseSection = () => {
    const { t } = useTranslation();

    const features = [
        {
            id: "creation",
            icon: Video,
            title: t('landing.featuresShowcase.features.creation.title'),
            shortDesc: t('landing.featuresShowcase.features.creation.shortDesc'),
            description: t('landing.featuresShowcase.features.creation.description'),
            highlights: t('landing.featuresShowcase.features.creation.highlights', { returnObjects: true }) as string[]
        },
        {
            id: "monetization",
            icon: DollarSign,
            title: t('landing.featuresShowcase.features.monetization.title'),
            shortDesc: t('landing.featuresShowcase.features.monetization.shortDesc'),
            description: t('landing.featuresShowcase.features.monetization.description'),
            highlights: t('landing.featuresShowcase.features.monetization.highlights', { returnObjects: true }) as string[]
        },
        {
            id: "audience",
            icon: Users,
            title: t('landing.featuresShowcase.features.audience.title'),
            shortDesc: t('landing.featuresShowcase.features.audience.shortDesc'),
            description: t('landing.featuresShowcase.features.audience.description'),
            highlights: t('landing.featuresShowcase.features.audience.highlights', { returnObjects: true }) as string[]
        },
        {
            id: "live",
            icon: Headphones,
            title: t('landing.featuresShowcase.features.live.title'),
            shortDesc: t('landing.featuresShowcase.features.live.shortDesc'),
            description: t('landing.featuresShowcase.features.live.description'),
            highlights: t('landing.featuresShowcase.features.live.highlights', { returnObjects: true }) as string[]
        },
        {
            id: "analytics",
            icon: BarChart3,
            title: t('landing.featuresShowcase.features.analytics.title'),
            shortDesc: t('landing.featuresShowcase.features.analytics.shortDesc'),
            description: t('landing.featuresShowcase.features.analytics.description'),
            highlights: t('landing.featuresShowcase.features.analytics.highlights', { returnObjects: true }) as string[]
        },
        {
            id: "protection",
            icon: Shield,
            title: t('landing.featuresShowcase.features.protection.title'),
            shortDesc: t('landing.featuresShowcase.features.protection.shortDesc'),
            description: t('landing.featuresShowcase.features.protection.description'),
            highlights: t('landing.featuresShowcase.features.protection.highlights', { returnObjects: true }) as string[]
        }
    ];

    const [activeFeatureIndex, setActiveFeatureIndex] = useState(0);
    const activeFeature = features[activeFeatureIndex];

    return (
        <section className="py-16 lg:py-36 bg-muted/30 relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-40">
                <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent" />
                <div className="absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
            </div>

            {/* Decorative Elements */}
            <div className="hidden sm:block absolute top-20 right-10 w-20 h-20 border border-primary/20 rounded-full animate-pulse" />
            <div className="hidden sm:block absolute bottom-40 left-10 w-12 h-12 border border-primary/10 rounded-lg rotate-45" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-20">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-6">
                        <Sparkles className="w-4 h-4" />
                        {t('landing.featuresShowcase.badge')}
                    </div>
                    <h2 className="font-display text-3xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-4 sm:mb-6 tracking-tight">
                        {t('landing.featuresShowcase.titlePart1')}
                        <span className="text-gradient block sm:inline"> {t('landing.featuresShowcase.titlePart2')}</span>
                    </h2>
                    <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
                        {t('landing.featuresShowcase.subtitle')}
                    </p>
                </div>

                {/* Features Content */}
                <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                    {/* Features List - Left Side */}
                    <div className="lg:col-span-5 xl:col-span-4">
                        <div className="bg-card/50 backdrop-blur-sm rounded-3xl border border-border/50 p-2 lg:p-4 shadow-xl">
                            <div className="space-y-1 lg:space-y-2">
                                {features.map((feature, index) => {
                                    const Icon = feature.icon;
                                    const isActive = activeFeatureIndex === index;

                                    return (
                                        <button
                                            key={feature.id}
                                            onClick={() => setActiveFeatureIndex(index)}
                                            className={cn(
                                                "w-full text-left p-3 lg:p-5 rounded-2xl transition-all duration-500 group relative overflow-hidden",
                                                isActive
                                                    ? "bg-primary text-primary-foreground shadow-lg shadow-primary/25"
                                                    : "hover:bg-muted/80"
                                            )}
                                            style={{ animationDelay: `${index * 0.05}s` }}
                                        >
                                            {/* Active indicator line */}
                                            <div className={cn(
                                                "absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full transition-all duration-300",
                                                isActive ? "bg-primary-foreground opacity-100" : "opacity-0"
                                            )} />

                                            <div className="flex items-center gap-3 lg:gap-4">
                                                <div className={cn(
                                                    "w-10 h-10 lg:w-12 lg:h-12 rounded-xl flex items-center justify-center transition-all duration-300 flex-shrink-0",
                                                    isActive
                                                        ? "bg-primary-foreground/20"
                                                        : "bg-primary/10 group-hover:bg-primary/15"
                                                )}>
                                                    <Icon className={cn(
                                                        "transition-all duration-300",
                                                        isActive ? "text-primary-foreground" : "text-primary"
                                                    )} size={isActive ? 22 : 20} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <h3 className={cn(
                                                        "font-display font-semibold text-sm lg:text-lg transition-colors duration-300",
                                                        isActive ? "text-primary-foreground" : "text-foreground"
                                                    )}>
                                                        {feature.title}
                                                    </h3>
                                                    <p className={cn(
                                                        "text-xs lg:text-sm truncate transition-colors duration-300",
                                                        isActive ? "text-primary-foreground/70" : "text-muted-foreground"
                                                    )}>
                                                        {feature.shortDesc}
                                                    </p>
                                                </div>
                                                <ChevronRight className={cn(
                                                    "w-4 h-4 lg:w-5 lg:h-5 transition-all duration-300 flex-shrink-0",
                                                    isActive
                                                        ? "text-primary-foreground opacity-100 translate-x-0"
                                                        : "text-muted-foreground opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0"
                                                )} />
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Feature Details - Right Side */}
                    <div className="lg:col-span-7 xl:col-span-8">
                        <div
                            key={activeFeature.id}
                            className="bg-card rounded-3xl border border-border/50 shadow-2xl shadow-primary/5 overflow-hidden animate-fade-in"
                        >
                            {/* Feature Header with gradient background */}
                            <div className="bg-gradient-to-br from-primary/5 via-primary/10 to-transparent p-6 lg:p-10 border-b border-border/50">
                                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                                    <div className="w-14 h-14 lg:w-20 lg:h-20 rounded-2xl flex items-center justify-center bg-primary shadow-xl shadow-primary/30 flex-shrink-0">
                                        <activeFeature.icon className="text-primary-foreground w-7 h-7 lg:w-9 lg:h-9" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-display text-xl lg:text-4xl font-bold text-foreground mb-2 lg:mb-3 tracking-tight">
                                            {activeFeature.title}
                                        </h3>
                                        <p className="text-muted-foreground text-sm lg:text-xl leading-relaxed">
                                            {activeFeature.description}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Feature Highlights */}
                            <div className="p-6 lg:p-10">
                                <h4 className="text-[10px] lg:text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4 lg:mb-6 flex items-center gap-2">
                                    <div className="w-6 h-[2px] lg:w-8 lg:h-[2px] bg-primary rounded-full" />
                                    {t('landing.featuresShowcase.includedTitle')}
                                </h4>
                                <div className="grid xs:grid-cols-2 gap-3 lg:gap-4">
                                    {activeFeature.highlights && Array.isArray(activeFeature.highlights) && activeFeature.highlights.map((highlight, index) => (
                                        <div
                                            key={index}
                                            className="flex items-start gap-2 lg:gap-3 p-3 lg:p-4 rounded-xl bg-muted/50 hover:bg-muted transition-colors duration-300 animate-fade-in group"
                                            style={{ animationDelay: `${index * 0.08}s` }}
                                        >
                                            <div className="w-5 h-5 lg:w-6 lg:h-6 rounded-full flex items-center justify-center flex-shrink-0 bg-primary/10 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                                                <Check className="w-3 h-3 lg:w-3.5 lg:h-3.5 text-primary group-hover:text-primary-foreground transition-colors duration-300" />
                                            </div>
                                            <span className="text-foreground font-medium text-xs lg:text-base leading-tight">{highlight}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA */}
                                <div className="mt-8 lg:mt-10 pt-6 lg:pt-8 border-t border-border/50 flex flex-col sm:flex-row gap-4">
                                    <Link to="/auth?mode=register" className="w-full sm:w-auto">
                                        <button className="w-full inline-flex items-center justify-center gap-2 px-8 py-3 lg:py-4 rounded-xl font-semibold text-primary-foreground bg-primary transition-all duration-300 hover:shadow-xl hover:shadow-primary/25 hover:-translate-y-1 group">
                                            {t('landing.featuresShowcase.cta')}
                                            <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                        </button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturesShowcaseSection;
